import React, { useState, useEffect, useMemo } from 'react';
import { FiArrowLeft, FiSave } from 'react-icons/fi';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { imageHandler, uploadImageToCloudinary } from '../../utils/cloudinaryUpload';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const CATEGORIES = ['VoIP', 'Calling Tips', 'Product Updates', 'Business', 'Security', 'Guides'];

const AdminBlogEditor = ({ postId, onBack }) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('content');
    const [tagInput, setTagInput] = useState('');

    const [form, setForm] = useState({
        title: '',
        slug: '',
        excerpt: '',
        content: '',
        author: 'Ringez Team',
        category: 'VoIP',
        tags: [],
        featuredImage: '',
        seo: { metaTitle: '', metaDescription: '', focusKeyword: '', canonicalUrl: '' },
        published: false
    });

    const token = localStorage.getItem('adminToken');

    // ReactQuill toolbar config with Cloudinary image handler
    const modules = useMemo(() => ({
        toolbar: {
            container: [
                [{ header: [1, 2, 3, false] }],
                ['bold', 'italic', 'underline', 'strike'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                ['link', 'image', 'blockquote', 'code-block'],
                ['clean']
            ],
            handlers: {
                image: imageHandler
            }
        }
    }), []);

    // Load post if editing
    useEffect(() => {
        if (postId) {
            setLoading(true);
            fetch(`${API_URL}/blogs?all=true&limit=100`)
                .then(r => r.json())
                .then(data => {
                    if (data.success) {
                        const post = data.data.find(p => p._id === postId);
                        if (post) setForm(prev => ({ ...prev, ...post, seo: post.seo || prev.seo }));
                    }
                })
                .catch(console.error)
                .finally(() => setLoading(false));
        }
    }, [postId]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name.startsWith('seo.')) {
            const field = name.split('.')[1];
            setForm(prev => ({ ...prev, seo: { ...prev.seo, [field]: value } }));
        } else {
            setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        }
        // Auto-generate slug from title on create
        if (name === 'title' && !postId) {
            const slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
            setForm(prev => ({ ...prev, slug }));
        }
    };

    const addTag = () => {
        const tag = tagInput.trim();
        if (tag && !form.tags.includes(tag)) {
            setForm(prev => ({ ...prev, tags: [...prev.tags, tag] }));
            setTagInput('');
        }
    };

    const removeTag = (t) => setForm(prev => ({ ...prev, tags: prev.tags.filter(x => x !== t) }));

    const calcReadTime = (html) => {
        const words = html.replace(/<[^>]*>/g, '').split(/\s+/).length;
        return Math.ceil(words / 200);
    };

    const handleFeaturedImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            alert('Please select an image file.');
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            alert('Image size should be less than 10MB.');
            return;
        }

        try {
            const url = await uploadImageToCloudinary(file, process.env.REACT_APP_CLOUDINARY_BLOG_FEATURED_FOLDER);
            setForm(prev => ({ ...prev, featuredImage: url }));
            alert('Featured image uploaded successfully.');
        } catch (err) {
            console.error('Featured image upload failed', err);
            alert('Featured image upload failed. Please try again.');
        } finally {
            // reset file input so the same file can be re-selected if needed
            e.target.value = '';
        }
    };

    const handleSubmit = async (e, publishNow = null) => {
        if (e) e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                ...form,
                readTime: calcReadTime(form.content),
                published: publishNow !== null ? publishNow : form.published
            };
            if (payload.published && !payload.publishedAt) {
                payload.publishedAt = new Date();
            }

            const url = postId ? `${API_URL}/blogs/${postId}` : `${API_URL}/blogs`;
            const method = postId ? 'PUT' : 'POST';

            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (data.success) {
                alert(postId ? 'Post updated!' : 'Post created!');
                onBack();
            } else {
                alert(data.message || 'Save failed. Check all required fields.');
            }
        } catch (err) {
            alert('Network error. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const s = {
        container: { paddingBottom: '2rem' },
        header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' },
        backBtn: {
            display: 'flex', alignItems: 'center', gap: '6px', background: 'none',
            border: '1px solid var(--border)', color: 'var(--text-muted)',
            borderRadius: '8px', padding: '8px 14px', cursor: 'pointer', fontSize: '0.875rem'
        },
        actionBtns: { display: 'flex', gap: '0.5rem' },
        draftBtn: {
            padding: '9px 16px', borderRadius: '8px', background: 'transparent',
            border: '1px solid var(--border)', color: 'var(--text-muted)', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem'
        },
        publishBtn: {
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '9px 18px', borderRadius: '8px', background: 'var(--primary)',
            border: 'none', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem'
        },
        tabs: { display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: '1.5rem', gap: '0' },
        tab: (active) => ({
            padding: '10px 20px', cursor: 'pointer', background: 'none', border: 'none',
            color: active ? 'var(--primary)' : 'var(--text-muted)',
            borderBottom: active ? '2px solid var(--primary)' : '2px solid transparent',
            fontWeight: 600, fontSize: '0.9rem', transition: 'all 0.15s'
        }),
        card: {
            background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border)',
            borderRadius: '12px', padding: '1.25rem', marginBottom: '1rem'
        },
        label: { display: 'block', color: 'var(--text-muted)', fontSize: '0.825rem', fontWeight: 600, marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.04em' },
        input: {
            width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border)',
            borderRadius: '8px', padding: '10px 14px', color: 'var(--text)', fontSize: '0.95rem',
            outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s'
        },
        textarea: {
            width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border)',
            borderRadius: '8px', padding: '10px 14px', color: 'var(--text)', fontSize: '0.95rem',
            outline: 'none', resize: 'vertical', boxSizing: 'border-box', minHeight: '90px'
        },
        select: {
            width: '100%', background: 'rgba(15, 23, 42, 0.9)', border: '1px solid var(--border)',
            borderRadius: '8px', padding: '10px 14px', color: 'var(--text)', fontSize: '0.95rem', outline: 'none'
        },
        grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' },
        hint: { color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '4px' },
        tagChip: {
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            padding: '4px 10px', background: 'rgba(99,102,241,0.12)', color: '#818cf8',
            borderRadius: '9999px', fontSize: '0.8rem', marginRight: '6px', marginBottom: '6px'
        },
        tagX: { background: 'none', border: 'none', color: '#818cf8', cursor: 'pointer', fontSize: '1rem', lineHeight: 1 },
        seoPreview: {
            background: '#0f172a', border: '1px solid #334155', borderRadius: '10px', padding: '1rem', marginTop: '1rem'
        },
        seoUrl: { color: '#4ade80', fontSize: '0.82rem', marginBottom: '2px' },
        seoTitle: { color: '#60a5fa', fontSize: '1rem', fontWeight: 600, marginBottom: '4px' },
        seoDesc: { color: '#94a3b8', fontSize: '0.85rem' },
        publishToggle: { display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' },
        checkmark: { width: 18, height: 18, accentColor: 'var(--primary)', cursor: 'pointer' }
    };

    if (loading) return <p style={{ color: 'var(--text-muted)', padding: '2rem' }}>Loading post…</p>;

    return (
        <div style={s.container}>
            {/* Header */}
            <div style={s.header}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <button style={s.backBtn} onClick={onBack}><FiArrowLeft size={16} /> Back</button>
                    <h2 style={{ color: 'var(--text)', fontSize: '1.25rem', fontWeight: 700 }}>
                        {postId ? 'Edit Post' : 'New Blog Post'}
                    </h2>
                </div>
                <div style={s.actionBtns}>
                    <button
                        style={s.draftBtn}
                        onClick={(e) => { setForm(p => ({ ...p, published: false })); handleSubmit(e, false); }}
                        disabled={saving}
                    >
                        Save Draft
                    </button>
                    <button
                        style={s.publishBtn}
                        onClick={(e) => { setForm(p => ({ ...p, published: true })); handleSubmit(e, true); }}
                        disabled={saving}
                    >
                        <FiSave size={15} /> {saving ? 'Saving…' : 'Publish'}
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div style={s.tabs}>
                <button style={s.tab(activeTab === 'content')} onClick={() => setActiveTab('content')}>Content</button>
                <button style={s.tab(activeTab === 'seo')} onClick={() => setActiveTab('seo')}>SEO Settings</button>
            </div>

            {/* Content Tab */}
            {activeTab === 'content' && (
                <form id="blog-form" onSubmit={handleSubmit}>
                    {/* Title */}
                    <div style={s.card}>
                        <label style={s.label}>Title *</label>
                        <input style={{ ...s.input, fontSize: '1.2rem', fontWeight: 700 }} name="title" value={form.title} onChange={handleChange} placeholder="Enter post title…" required />
                    </div>

                    {/* Slug */}
                    <div style={s.card}>
                        <label style={s.label}>URL Slug *</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ color: 'var(--text-muted)', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>ringez.com/blog/</span>
                            <input style={s.input} name="slug" value={form.slug} onChange={handleChange} placeholder="url-slug" required />
                        </div>
                    </div>

                    {/* Excerpt */}
                    <div style={s.card}>
                        <label style={s.label}>Excerpt *</label>
                        <textarea style={s.textarea} name="excerpt" value={form.excerpt} onChange={handleChange} placeholder="Brief summary of the post (shown on blog listing)…" required />
                        <p style={s.hint}>{form.excerpt.length}/160 characters</p>
                    </div>

                    {/* Rich Text Editor */}
                    <div style={{ ...s.card, paddingBottom: '60px' }}>
                        <label style={s.label}>Content *</label>
                        <div style={{ background: '#fff', borderRadius: '8px' }}>
                            <ReactQuill
                                theme="snow"
                                value={form.content}
                                onChange={(content) => setForm(prev => ({ ...prev, content }))}
                                modules={modules}
                                style={{ minHeight: '350px' }}
                            />
                        </div>
                    </div>

                    {/* Author & Category */}
                    <div style={s.grid2}>
                        <div style={s.card}>
                            <label style={s.label}>Author *</label>
                            <input style={s.input} name="author" value={form.author} onChange={handleChange} placeholder="Author name" required />
                        </div>
                        <div style={s.card}>
                            <label style={s.label}>Category *</label>
                            <select style={s.select} name="category" value={form.category} onChange={handleChange} required>
                                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                    </div>

                    {/* Tags */}
                    <div style={s.card}>
                        <label style={s.label}>Tags</label>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                            <input
                                style={s.input}
                                value={tagInput}
                                onChange={(e) => setTagInput(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                                placeholder="Type a tag and press Enter"
                            />
                            <button type="button" onClick={addTag} style={{ ...s.publishBtn, whiteSpace: 'nowrap' }}>Add</button>
                        </div>
                        <div>
                            {form.tags.map((t, i) => (
                                <span key={i} style={s.tagChip}>
                                    {t}<button type="button" style={s.tagX} onClick={() => removeTag(t)}>×</button>
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Featured Image (Cloudinary + URL) */}
                    <div style={s.card}>
                        <label style={s.label}>Featured Image</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.75rem' }}>
                            <label
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '0.6rem 1.2rem',
                                    background: 'var(--primary)',
                                    color: '#fff',
                                    borderRadius: '0.5rem',
                                    cursor: 'pointer',
                                    fontWeight: 600,
                                    fontSize: '0.9rem',
                                    width: 'fit-content'
                                }}
                            >
                                📷 Upload from computer
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFeaturedImageUpload}
                                    style={{ display: 'none' }}
                                />
                            </label>
                            <span style={s.hint}>
                                Image will be uploaded to Cloudinary in the Ringez folder.  
                                You can also paste a direct image URL below.
                            </span>
                        </div>
                        <input
                            style={s.input}
                            type="url"
                            name="featuredImage"
                            value={form.featuredImage}
                            onChange={handleChange}
                            placeholder="https://your-image.jpg"
                        />
                        {form.featuredImage && (
                            <img
                                src={form.featuredImage}
                                alt="Preview"
                                style={{
                                    width: '100%',
                                    maxWidth: 400,
                                    height: 200,
                                    objectFit: 'cover',
                                    borderRadius: '8px',
                                    marginTop: '10px'
                                }}
                                onError={e => { e.target.style.display = 'none'; }}
                            />
                        )}
                    </div>

                    {/* Publish Toggle */}
                    <div style={s.card}>
                        <label style={s.publishToggle}>
                            <input type="checkbox" name="published" checked={form.published} onChange={handleChange} style={s.checkmark} />
                            <span style={{ color: 'var(--text)', fontWeight: 600 }}>Publish immediately</span>
                        </label>
                    </div>
                </form>
            )}

            {/* SEO Tab */}
            {activeTab === 'seo' && (
                <div>
                    <div style={s.card}>
                        <label style={s.label}>Meta Title <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(max 60 chars)</span></label>
                        <input style={s.input} name="seo.metaTitle" value={form.seo.metaTitle} onChange={handleChange} placeholder="Ringez | VoIP Tip Title" maxLength={60} />
                        <p style={s.hint}>{form.seo.metaTitle.length}/60</p>
                    </div>
                    <div style={s.card}>
                        <label style={s.label}>Meta Description <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(max 160 chars)</span></label>
                        <textarea style={s.textarea} name="seo.metaDescription" value={form.seo.metaDescription} onChange={handleChange} placeholder="SEO description for search engines" maxLength={160} />
                        <p style={s.hint}>{form.seo.metaDescription.length}/160</p>
                    </div>
                    <div style={s.grid2}>
                        <div style={s.card}>
                            <label style={s.label}>Focus Keyword</label>
                            <input style={s.input} name="seo.focusKeyword" value={form.seo.focusKeyword} onChange={handleChange} placeholder="e.g. international voip calls" />
                        </div>
                        <div style={s.card}>
                            <label style={s.label}>Canonical URL</label>
                            <input style={s.input} name="seo.canonicalUrl" value={form.seo.canonicalUrl} onChange={handleChange} placeholder="https://ringez.com/blog/your-slug" />
                        </div>
                    </div>

                    {/* Google Preview */}
                    <div style={s.card}>
                        <label style={s.label}>Google Search Preview</label>
                        <div style={s.seoPreview}>
                            <div style={s.seoUrl}>ringez.com › blog › {form.slug || 'your-slug'}</div>
                            <div style={s.seoTitle}>{form.seo.metaTitle || form.title || 'Your Post Title'}</div>
                            <div style={s.seoDesc}>{form.seo.metaDescription || form.excerpt || 'Your post description appears here…'}</div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminBlogEditor;
